import type {URI} from '@microsoft/agent-host-protocol'
import type {JsonRpcMessage} from '@microsoft/agent-host-protocol/client'

export function isDisplayableSessionTitle(title: unknown, sessionId: string): title is string {
  return typeof title === 'string' && title.trim().length > 0 && title.trim() !== sessionId
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export class RelaySessionTitle {
  readonly #sessionId: string
  #title: string | undefined
  #receivedLiveTitle = false
  #receivedWireTitle = false

  constructor(sessionId: string) {
    this.#sessionId = sessionId
  }

  get title(): string | undefined {
    return this.#title
  }

  reset(): void {
    this.#title = undefined
    this.#receivedLiveTitle = false
    this.#receivedWireTitle = false
  }

  observeMessage(message: JsonRpcMessage, session: URI): void {
    if (!('method' in message) || 'id' in message || !isRecord(message.params)) return
    const params = message.params
    let title: unknown
    if (
      message.method === 'action' &&
      params.channel === session &&
      !params.rejectionReason &&
      isRecord(params.action) &&
      params.action.type === 'session/titleChanged'
    ) {
      title = params.action.title
    } else if (
      message.method === 'root/sessionSummaryChanged' &&
      params.channel === 'ahp-root://' &&
      params.session === session &&
      isRecord(params.changes)
    ) {
      title = params.changes.title
    }
    if (!isDisplayableSessionTitle(title, this.#sessionId)) return
    this.#title = title
    this.#receivedLiveTitle = true
    this.#receivedWireTitle = true
  }

  acceptSnapshotTitle(title: string | undefined): void {
    if (!this.#receivedLiveTitle && isDisplayableSessionTitle(title, this.#sessionId)) this.#title = title
  }

  acceptSubscriptionTitle(title: string, live = false): string | undefined {
    // Subscription readers can drain in a different order from wire delivery.
    // Replay actions have no wire notification, so accept them until live metadata arrives.
    if (!this.#receivedWireTitle && isDisplayableSessionTitle(title, this.#sessionId)) {
      this.#title = title
      this.#receivedLiveTitle ||= live
    }
    return this.#title
  }
}
